import { api, getAiApiUrl } from '@/lib/utils/api.utils';

// Use Hyperliquid position type for consistency
export interface StorySimpleItem {
  count: number;
  symbol: string;
  totalPnl: number;
}

export interface StorySimpleResponse {
  list: StorySimpleItem[];
}

export interface StoryListRequest {
  limit?: number;
  offset?: number;
  /**
   * 按照pnl或者time进行排序
   */
  sortBy?: string;
  /**
   * asc从小到大 desc从大到小
   */
  sortOrder?: string;
}

export interface StoryListItem {
  analysis: string;
  closedAt: number;
  id: number;
  leverage: number;
  openedAt: number;
  pnl: number;
  pnlPercentage: number;
  side: string;
  title: string;
}

export interface StoryListResponse {
  list: StoryListItem[];
  total: number;
  totalPnl: number;
  winRate: number;
}

export interface SymbolStoryListRequest {
  limit?: number;
  offset?: number;
}

export interface SymbolStoryListItem {
  address: string;
  closedAt: number;
  id: number;
  leverage: number;
  openedAt: number;
  pnl: number;
  pnlPercentage: number;
  side: string;
  symbol: string;
  title: string;
  nickname: string;
  avgOpenPrice: number;
  avgClosePrice: number;
}

export interface SymbolStoryListResponse {
  list: SymbolStoryListItem[];
  total: number;
  [property: string]: any;
}

export interface Order {
  id: number;
  orderType: string;
  side: string;
  size: number;
  price: number;
  leverage: number;
  createdAt: number;
  closedPnl: number;
}

export interface StoryDetailResponse {
  id: number;
  address: string;
  symbol: string;
  pnl: number;
  leverage: number;
  nickname: string;
  pnlPercentage: number;
  avgOpenPrice: number;
  avgClosePrice: number;
  side: string;
  title: string;
  analysis: string;
  orders: Order[];
}

/**
 * Fetches story detail by ID from the new API endpoint
 * @param storyId - The unique identifier for the story
 * @returns Promise containing the story detail response
 */
export async function fetchStoryDetailById(
  storyId: string,
): Promise<StoryDetailResponse> {
  const response = await api.get(
    `${getAiApiUrl()}/tradestory/detail/${storyId}`,
  );
  const data = await response.json<StoryDetailResponse>();
  if (!data) {
    throw new Error('Failed to fetch story detail');
  }
  return data;
}

/**
 * Fetches simple story data for a trader address
 * @param address - The trader's address
 * @returns Promise containing the simple story response
 */
export async function fetchStorySimple(
  address: string,
): Promise<StorySimpleResponse> {
  const response = await api.get(
    `${getAiApiUrl()}/tradestory/simple/${address}`,
  );
  return await response.json<StorySimpleResponse>();
}

/**
 * Fetches paginated story list for a trader address and symbol
 * @param address - The trader's address
 * @param symbol - The trading symbol
 * @param params - Query parameters for pagination and sorting
 * @returns Promise containing the story list response
 */
export async function fetchStoryList(
  address: string,
  symbol: string,
  params: StoryListRequest = {},
): Promise<StoryListResponse> {
  const url = new URL(
    `${getAiApiUrl()}/tradestory/address/symbol/list/${address}/${symbol}`,
  );

  // Add query parameters
  if (params.limit !== undefined)
    url.searchParams.set('limit', params.limit.toString());
  if (params.offset !== undefined)
    url.searchParams.set('offset', params.offset.toString());
  if (params.sortBy) url.searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) url.searchParams.set('sortOrder', params.sortOrder);

  // Add any additional parameters
  Object.entries(params).forEach(([key, value]) => {
    if (
      !['limit', 'offset', 'sortBy', 'sortOrder'].includes(key) &&
      value !== undefined
    ) {
      url.searchParams.set(key, String(value));
    }
  });

  const response = await api.get(url.toString());
  return await response.json<StoryListResponse>();
}

/**
 * Fetches paginated story list for a specific symbol
 * @param symbol - The trading symbol
 * @param params - Query parameters for pagination
 * @returns Promise containing the symbol story list response
 */
export async function fetchSymbolStoryList(
  symbol: string,
  params: SymbolStoryListRequest = {},
): Promise<SymbolStoryListResponse> {
  const url = new URL(`${getAiApiUrl()}/tradestory/symbol/list/${symbol}`);

  // Add query parameters
  if (params.limit !== undefined)
    url.searchParams.set('limit', params.limit.toString());
  if (params.offset !== undefined)
    url.searchParams.set('offset', params.offset.toString());

  // Add any additional parameters
  Object.entries(params).forEach(([key, value]) => {
    if (!['limit', 'offset'].includes(key) && value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  });

  const response = await api.get(url.toString());
  return await response.json<SymbolStoryListResponse>();
}
